import { calculateContainerHeight, calculateFontSize, formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import Legend from 'components/molecules/Legend';
import { graphItemContainerClass, subHeaderClass } from 'components/pages/PVMAnalysis';
import InfiniteChartComponent from 'components/molecules/InfiniteChartComponent/InfiniteChartComponent';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import { useFetchAttributeLevelElasticity } from 'hooks/opportunityIdentification/useFetchAttributeLvlElasticity';
import { FilterContextData } from 'contexts/FilterContext';
import { OPPORTUNITY_IDENTIFICATION_CHART1_VALUE_SELECTED } from 'utils/mixpanelEventNames';
import { trackEventMixpanel } from 'utils/mixpanel';

type ElasticityGroupBarChart1Props = {
    selectedChartItem: any;
    setSelectedChartItem: Function;
    selectedLevel: any;
    loading: boolean;
};

const ElasticityGroupBarChart1 = (props: ElasticityGroupBarChart1Props) => {
    const { selectedChartItem, setSelectedChartItem, selectedLevel } = props;
    const { appConfig } = useContext(UserContext);
    const elasticityConfig = appConfig.opportunityIdentification.elasticityDriver;
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const showFullLabel = commonConfig?.showFullLabel;

    const chart1OffsetRef = useRef(elasticityConfig.offset || 0);
    const chart1Ref = useRef<any>();

    const { allDimAndAttributeTypeList, idForRestOfComponent, timePeriodSelection, idForRestOfComponentLoading } =
        useContext(FilterContextData);

    const {
        data: chart1Data,
        loading: chart1Loading,
        error: chart1Error,
        count: chart1Count,
        meta,
        fetchAttributeLevelElasticity,
    } = useFetchAttributeLevelElasticity();

    useEffect(() => {
        if (chart1Data?.length) {
            setSelectedChartItem(chart1Data[0]);
        }
    }, [chart1Data]);

    const variables = useMemo(() => {
        if (!idForRestOfComponentLoading) {
            const filters = JSON.stringify(idForRestOfComponent);
            return {
                filters: filters,
                timePeriod: String(timePeriodSelection).toLowerCase(),
                dimension: selectedLevel.dimension,
                attributeType: selectedLevel.value,
                limit: elasticityConfig.limit,
                offset: chart1OffsetRef.current,
            };
        }
    }, [
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allDimAndAttributeTypeList?.loading,
        timePeriodSelection,
        selectedLevel.value,
    ]);

    useEffect(() => {
        fetchAttributeLevelElasticity({
            variables,
        });
        chart1OffsetRef.current = 0;
    }, [variables]);

    useEffect(() => {
        if (chart1Error) {
            setSelectedChartItem('error');
        }
    }, [chart1Error]);

    const combinedLoading = chart1Loading;
    const height = Math.max(450, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200
    const formattedChartLabel = `Base Price Elasticities by ${selectedLevel.label}`;

    //scale y axis w.r.t the max characters to avoid collision of names  (only for chart 2)
    const barHeight2WRTCharacters = Math.max(meta?.maxCharacters * (77 / 39) || 0, 77); // 39 characters can be accommodated in 77px height
    const barHeight2 = Math.max(
        barHeight2WRTCharacters,
        Array.isArray(chart1Data) && chart1Data?.length > 0 ? height / chart1Data?.length : 0,
    );
    const yAxisGraphWidth = calculateFontSize(window.innerWidth, 120);
    const containerWidth = useContainerWidth({
        containerId: 'random id' + 'q', //giving an incorrect id so that it takes from main container by default
        buffer: 16 * 3 + 2, //padding 16 * 3 + border 2
    });

    //infinite scroll function
    function infiniteScrollOnNext(e: any) {
        if (chart1Loading) return;
        //finding the scrollbar position
        const scrollHeight = e?.scrollHeight; //total height
        const clientHeight = e?.clientHeight; //viewport height
        const scrollBarBottomPosition = clientHeight + e?.scrollTop;
        const threshold = 0.95;

        if (scrollBarBottomPosition >= scrollHeight * threshold && chart1Data?.length < meta.total) {
            const newOffset = elasticityConfig.limit + chart1OffsetRef.current;
            fetchAttributeLevelElasticity({
                variables: {
                    ...variables,
                    offset: newOffset,
                },
            });
            chart1OffsetRef.current = newOffset;
        }
    }

    //debounced infinite scroller function
    const debouncedInfiniteScroller = useCallback(_.debounce(infiniteScrollOnNext, 150), [
        chart1Count,
        chart1Data,
        combinedLoading,
    ]);

    const chartWidth = containerWidth / 2 - 16 * 3; //subtract inner padding for 2 charts

    return (
        <div className={`${graphItemContainerClass} mb-0 flex-1`}>
            {chart1Error && !combinedLoading && !chart1Data ? (
                <div style={{ height: height + 178, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ErrorBox msg={String(chart1Error)} />
                </div>
            ) : // LOADING
            combinedLoading ? (
                <div style={{ height: height + 178 }}>
                    <Loader />
                </div>
            ) : (
                <div ref={chart1Ref}>
                    <div className="flex justify-between">
                        <p className={subHeaderClass}> {formattedChartLabel} </p>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: 14, marginBottom: 24 }}>
                        <div className="w-[35px]"></div>
                        <Legend data={elasticityConfig.legendMapping} />
                    </div>
                    <InfiniteChartComponent
                        isGroupedChart
                        rowCount={chart1Data?.length || 0}
                        minBound={meta?.minBound - 0.5}
                        maxBound={meta?.maxBound}
                        intervalFormatter={(val: any) => {
                            formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        tooltipFormatter={(xAxis: any, val: any) => {
                            return {
                                name: xAxis,
                                value: formatNumWithKMBAlt(val, decimalForPercentage) + '%',
                            };
                        }}
                        intervalStyle={() => {
                            return {
                                cursor: 'pointer',
                            };
                        }}
                        height={height}
                        rowRenderer={(key, index, style) => {
                            return chart1Data && chart1Data[index];
                        }}
                        chartItemOnClick={(item: any) => {
                            trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART1_VALUE_SELECTED, {
                                attributeTypeName: item?.label,
                            });
                            setSelectedChartItem(item);
                        }}
                        chartContainer={'drilldown-chart-virtualized1'}
                        barHeight={barHeight2}
                        chartWidth={chartWidth}
                        yAxisGraphWidth={yAxisGraphWidth}
                        selectedChartItem={selectedChartItem}
                        showFullLabel={showFullLabel}
                        debouncedInfiniteScroller={debouncedInfiniteScroller}
                        chartOffsetRef={chart1OffsetRef}
                        appConfig={appConfig}
                        fields={['category', 'sales']}
                        xAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ElasticityGroupBarChart1;
